/**
 * PackCover — AI cover art for a marketplace stem pack.
 * Shows the generated image, a shimmer while Nano Banana is painting, and a
 * genre-tinted placeholder (with an owner-only "Generate cover" button) when
 * there is no cover yet.
 */
import React, { useState } from "react";
import { Music2, Sparkles, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { packCoverUrl, regeneratePackCover } from "@/lib/marketplaceApi";

const GENRE_TINT = {
  "hip-hop": "from-amber-500/40 to-rose-600/40", trap: "from-rose-600/40 to-purple-700/40",
  rnb: "from-fuchsia-500/40 to-indigo-700/40", pop: "from-pink-400/40 to-cyan-500/40",
  rock: "from-orange-600/40 to-slate-800/40", edm: "from-cyan-400/40 to-fuchsia-600/40",
  house: "from-emerald-400/40 to-cyan-600/40", techno: "from-slate-500/40 to-cyan-800/40",
  ambient: "from-sky-400/30 to-indigo-800/40", "lo-fi": "from-amber-300/30 to-violet-700/40",
  jazz: "from-amber-500/40 to-emerald-800/40", soul: "from-orange-400/40 to-fuchsia-700/40",
};

export default function PackCover({ pack, size = "banner", canGenerate = false, onUpdated, className = "" }) {
  const [busy, setBusy] = useState(false);
  const url = packCoverUrl(pack);
  const pending = pack?.cover_status === "pending";
  const tint = GENRE_TINT[pack?.genre] || "from-violet-600/40 to-slate-900/60";
  const shape = size === "square" ? "aspect-square" : "aspect-[21/9]";

  const generate = async (e) => {
    e?.stopPropagation?.();
    setBusy(true);
    const id = toast.loading("Painting cover art…", { description: "Nano Banana is sketching from the title, genre and tags." });
    try {
      const fresh = await regeneratePackCover(pack.pack_id);
      toast.dismiss(id);
      toast.success("Cover art ready");
      onUpdated?.(fresh);
    } catch (err) {
      toast.dismiss(id);
      toast.error("Cover generation failed", { description: err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="pack-cover"
      data-cover-status={url ? "ready" : pending || busy ? "pending" : "none"}
      className={`relative overflow-hidden bg-gradient-to-br ${tint} ${shape} ${className}`}
    >
      {url ? (
        <img
          data-testid="pack-cover-img"
          src={url}
          alt={`${pack.title} cover art`}
          loading="lazy"
          className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.03]"
        />
      ) : (
        <div className="absolute inset-0 grid place-items-center">
          <div className="absolute inset-0 opacity-40 [background-image:radial-gradient(circle_at_30%_20%,rgba(255,255,255,.25),transparent_45%),radial-gradient(circle_at_75%_80%,rgba(255,255,255,.18),transparent_40%)]" />
          {pending || busy ? (
            <div data-testid="pack-cover-pending" className="relative flex items-center gap-2 rounded-full bg-black/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-white/80 backdrop-blur">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-fuchsia-200" /> Painting cover…
            </div>
          ) : (
            <Music2 className="relative h-8 w-8 text-white/35" />
          )}
        </div>
      )}
      {url && <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-slate-950/80 to-transparent" />}
      {canGenerate && !pending && !busy && (
        <button
          type="button"
          data-testid="pack-cover-generate-btn"
          onClick={generate}
          title={url ? "Regenerate cover art" : "Generate AI cover art"}
          className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-black/55 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-white/85 ring-1 ring-white/15 backdrop-blur transition hover:bg-fuchsia-500/40"
        >
          {url ? <RefreshCw className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />}
          {url ? "Redo" : "Generate cover"}
        </button>
      )}
    </div>
  );
}
